import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../lib/useStore.jsx';
import { appOf } from '../lib/store.js';
import { SKILLS, skillById, branchById } from '../content/skills.js';
import { progressOf, prereqsMet, isOwned, SOLO_FOR_MOSTLY, visibleSkills } from '../domain/skills.js';
import { Ladder, StateChip } from '../ui/Bits.jsx';
import { BackIcon, CheckIcon, StarIcon } from '../ui/Icons.jsx';

/** Steps are a lesson, not a chore: ticks live in this render only. */
function Steps({ steps, id }) {
  const [done, setDone] = useState(() => new Set());
  useEffect(() => setDone(new Set()), [id]);
  const toggle = (i) => setDone((d) => { const n = new Set(d); n.has(i) ? n.delete(i) : n.add(i); return n; });
  return (
    <ol className="steps">
      {steps.map((s, i) => (
        <li key={i} className={done.has(i) ? 'done' : ''}>
          <button type="button" className="step-tick" aria-pressed={done.has(i)} aria-label={`Step ${i + 1} done`} onClick={() => toggle(i)}>
            {done.has(i) ? <CheckIcon width="18" height="18" /> : i + 1}
          </button>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  );
}

export default function Skill() {
  const { id } = useParams();
  const nav = useNavigate();
  const { active, appendEvent, introduceSkill, parentUnlocked } = useStore();
  const skill = skillById(id);
  const app = active ? appOf(active) : null;
  const visible = app ? visibleSkills(SKILLS, app).some((s) => s.id === id) : false;
  const progress = app ? progressOf(app.events, id) : null;

  // Opening the steps is itself the first rung. introduceSkill is idempotent.
  useEffect(() => {
    if (active && skill && visible) introduceSkill(active.id, id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, active?.id, visible]);

  if (!skill || !active) return <p>That skill isn't here.</p>;
  if (!visible) {
    return (
      <div className="stack">
        <Link to="/" className="btn quiet small" style={{ justifySelf: 'start' }}><BackIcon width="20" height="20" /> back</Link>
        <div className="card"><h2>{skill.title}</h2><p className="muted">This one needs a grown-up to turn it on for {active.name} first.</p></div>
      </div>
    );
  }

  const branch = branchById(skill.branch);
  const owned = isOwned(progress.state);
  const ready = prereqsMet(skill, app.events);
  const privilege = app.privileges[id];
  const log = (withHelp) => appendEvent(active.id, { skillId: id, type: 'practiced', withHelp });
  const verify = () => appendEvent(active.id, { skillId: id, type: 'verified', by: 'parent' });
  const taught = () => appendEvent(active.id, { skillId: id, type: 'taught', by: 'parent' });
  const reset = () => appendEvent(active.id, { skillId: id, type: 'reset', by: 'parent', note: 'reset from skill page' });

  return (
    <div className="stack">
      <Link to="/" className="btn quiet small" style={{ justifySelf: 'start' }}><BackIcon width="20" height="20" /> {active.name}'s wall</Link>

      <section className={`card tint-${branch.color}`}>
        <p className="eyebrow">{branch.name}</p>
        <p className="cando">{skill.canDo}</p>
        <div className="row" style={{ marginTop: 10 }}><StateChip state={progress.state} />
          {owned && progress.verifiedAt && <span className="small muted">verified {progress.verifiedAt}</span>}
        </div>
        <div style={{ marginTop: 12 }}><Ladder state={progress.state} /></div>
      </section>

      {skill.safety !== 'free' && (
        <div className="banner">{skill.safety === 'adult_enables' ? skill.adultNote : 'A grown-up stays with you for this one.'}</div>
      )}

      {!ready && (
        <div className="card soft">
          <p className="eyebrow">First</p>
          <p>Get these to Independent: {skill.prereqs.map((p) => <Link key={p} to={`/skill/${p}`} style={{ marginRight: 8 }}>{skillById(p)?.title}</Link>)}</p>
        </div>
      )}

      <p className="muted">{skill.why}</p>

      <section className="card">
        <p className="eyebrow" style={{ marginBottom: 10 }}>How</p>
        <Steps steps={skill.steps} id={id} />
      </section>

      {privilege && (
        <div className="privilege"><b>{owned ? 'Because you can do this:' : "When you're independent at this:"}</b> {privilege}</div>
      )}

      {!owned && (
        <section className="stack">
          <div className="actions">
            <button className="btn" onClick={() => log(true)}>I did it with help</button>
            <button className="btn primary" onClick={() => log(false)}><CheckIcon width="20" height="20" /> I did it by myself</button>
          </div>
          <p className="hint">
            {progress.solo} of {SOLO_FOR_MOSTLY} on your own so far. {progress.state === 'mostly' ? 'Ask a grown-up to check you.' : ''}
          </p>
        </section>
      )}

      {parentUnlocked && (
        <section className="card soft stack">
          <p className="eyebrow">Grown-up</p>
          <p className="small"><b>Independent looks like:</b> {skill.independent}</p>
          <p className="small"><b>Tip:</b> {skill.teachTip}</p>
          <div className="actions">
            {!owned && <button className="btn accent" onClick={verify}><StarIcon width="20" height="20" /> They're independent</button>}
            {progress.state === 'independent' && <button className="btn accent" onClick={taught}>They taught someone</button>}
            {progress.started && <button className="btn" onClick={reset}>Start over</button>}
          </div>
        </section>
      )}
      {!parentUnlocked && progress.state === 'mostly' && (
        <button className="btn block" onClick={() => nav('/parent')}>Ask a grown-up to check</button>
      )}
    </div>
  );
}
